import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PinInput } from '@realtyka/real-ui';
import { QRCodeSVG } from 'qrcode.react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultSplashV2 from '../../../components/DefaultSplashV2';
import {
  EnableMfaRequestMfaTypeEnum,
  MfaControllerApi,
} from '../../../openapi/keymaker';
import ErrorService from '../../../services/ErrorService';
import { fetchKeymakerCurrentUser, mfaSignIn } from '../../../slices/AuthSlice';
import { useAppDispatch } from '../../../slices/store';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../slices/ToastNotificationSlice';
import { getCommonsApiErrorMessage } from '../../../utils/ErrorUtils';
import { handleUnauthorizedOnly } from '../../../utils/MfaErrorHandlingUtils';
import { getKeymakerConfiguration } from '../../../utils/OpenapiConfigurationUtils';

interface MfaAppPinCardProps {
  isForceMfa?: boolean;
  onPinVerified: () => void;
  redirectTo?: string;
  isAgentContext?: boolean;
}

const MfaAppPinCard: React.FC<MfaAppPinCardProps> = ({
  isForceMfa,
  onPinVerified,
  redirectTo,
  isAgentContext = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [qrcode, setQRcode] = useState<string>('');
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);

  const fetchQRCode = useCallback(async () => {
    try {
      const { data } = await new MfaControllerApi(
        await getKeymakerConfiguration(),
      ).getAuthenticatorQrCode();
      setQRcode(data);
    } catch (e: any) {
      // Handle authentication errors (401 Unauthorized only)
      const handled = await handleUnauthorizedOnly({
        error: e,
        dispatch,
        navigate,
        isAgentContext,
        redirectTo,
      });

      if (handled) {
        return;
      }

      // Handle other errors (including 403 Forbidden)
      ErrorService.notifyIgnoreAuthErrors(
        'Unable to fetch two factor authentication qr code',
        e,
      );
      dispatch(
        showErrorToast(
          'We had a problem fetching last two factor authentication qr code',
          'Please try again in a few moments.',
        ),
      );
    }
  }, [dispatch, isAgentContext, navigate, redirectTo]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  const submitCode = async (code: string) => {
    setIsLoading(true);
    setErrorMessage(undefined);
    const codeNumber = parseInt(code);
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).enableMfa({
        code: codeNumber,
        mfaType: EnableMfaRequestMfaTypeEnum.Authenticator,
      });
      if (isForceMfa) {
        await dispatch(mfaSignIn(codeNumber));
      }
      dispatch(fetchKeymakerCurrentUser());
      onPinVerified();
      dispatch(
        showSuccessToast('Successfully enabled two factor authentication.'),
      );
    } catch (e: any) {
      // Handle authentication errors (401 Unauthorized only)
      const handled = await handleUnauthorizedOnly({
        error: e,
        dispatch,
        navigate,
        isAgentContext,
        redirectTo,
      });

      if (handled) {
        return;
      }

      // Handle other errors (including 403 Forbidden)
      setErrorMessage(getCommonsApiErrorMessage(e));
      ErrorService.notify('Unable to enable two factor authentication', e, {
        data: {
          code: codeNumber,
          mfaType: EnableMfaRequestMfaTypeEnum.Authenticator,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-6 mb-4 mt-6'>
      <div className='flex flex-col gap-1 justify-center self-stretch'>
        <div className='text-ellipsis font-inter-bold text-[15px] leading-[22px]'>
          1. Scan the QR code below
        </div>
        <div className='font-inter-regular text-[15px] leading-[22px]'>
          {`On your authenticator app, add an account to select "Scan a QR code"`}
        </div>
        <div className='flex justify-center items-center p-6'>
          {qrcode ? (
            <QRCodeSVG value={qrcode!} size={208} data-testid='qrcode-svg' />
          ) : (
            <DefaultSplashV2 />
          )}
        </div>
      </div>
      <div className='flex flex-col gap-1 items-start self-stretch'>
        <div className='text-ellipsis font-inter-bold text-[15px] leading-[22px]'>
          2. Enter authentication code
        </div>
        <div className='font-inter-regular text-[15px] leading-[22px]'>
          Enter the code shown on the authenticator app
        </div>
        <div className='flex pt-2 justify-center items-center gap-2 self-stretch'>
          <PinInput
            onComplete={(code) => {
              hiddenInputRef.current?.focus();
              submitCode(code);
            }}
            error={errorMessage}
            data-testid='pin-input'
            disabled={isLoading}
          />
        </div>
        {errorMessage && (
          <div className='flex py-3 px-4 justify-center items-center gap-2 self-stretch rounded-[10px] bg-reskin-red-100 mt-1'>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className='text-reskin-red-600 text-sm md:text-base'
            />
            <div className='text-reskin-red-600 font-inter-medium text-sm md:text-base'>
              {errorMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MfaAppPinCard;
